import type { CompoundInputValue } from '../CompoundInput/types';
import type { InputType } from '../Input/types';
import type {
  FormFieldCheckboxGroupProps,
  FormFieldCompoundInputProps,
  FormFieldFileInputProps,
  FormFieldInputProps,
  FormFieldProps,
  FormFieldRadioGroupProps,
  FormFieldSelectProps,
  FormFieldTextAreaProps,
} from './types';

import { CheckboxGroup } from '../CheckboxGroup/CheckboxGroup';
import { CompoundInput } from '../CompoundInput/CompoundInput';
import { FileInput } from '../FileInput/FileInput';
import { Input } from '../Input/Input';
import { RadioGroup } from '../RadioGroup/RadioGroup';
import { Select } from '../Select/Select';
import { TextArea } from '../TextArea/TextArea';

type ControlPrivateProps = {
  ariaDescribedBy?: string;
  id: string;
};

export function isSelectFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldSelectProps<V> {
  return props.type === 'select';
}

export function isRadioGroupFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldRadioGroupProps<V> {
  return props.type === 'radio-group';
}

export function isCheckboxGroupFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldCheckboxGroupProps<V> {
  return props.type === 'checkbox-group';
}

export function isCompoundInputFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldCompoundInputProps<C> {
  return props.type === 'compound';
}

export function isTextAreaFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldTextAreaProps {
  return props.type === 'textarea';
}

export function isFileInputFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>): props is FormFieldFileInputProps {
  return props.type === 'file';
}

/**
 * Groups hold more than one control, so their label is a `div` pointed at by
 * `aria-labelledby` — a `<label for>` can only ever name one of them. The file
 * input joins them because the node carrying the field id is the upload area
 * (`role="group"`), not a labelable control.
 */
export function isGroupFormField<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(
  props: FormFieldProps<V, C>,
): props is
  | FormFieldRadioGroupProps<V>
  | FormFieldCheckboxGroupProps<V>
  | FormFieldCompoundInputProps<C>
  | FormFieldFileInputProps {
  return (
    isRadioGroupFormField(props) ||
    isCheckboxGroupFormField(props) ||
    isCompoundInputFormField(props) ||
    isFileInputFormField(props)
  );
}

function SelectControl<V>({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldSelectProps<V>;
}) {
  const selectPrivate = {
    ...privateProps,
    ariaLabelledBy: labelId,
  };

  // Spread rather than a hand-picked list: the wrapper's own type is
  // `Omit<SelectProps, '__private'>` plus the fields pulled out here, so a prop
  // added to Select reaches the control instead of being silently dropped.
  const { hint, layout, type, ...selectProps } = props;

  return <Select<V> {...selectProps} __private={selectPrivate} />;
}

function InputControl({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldInputProps<InputType>;
}) {
  return (
    <Input
      autoCapitalize={props.autoCapitalize}
      disabled={props.disabled}
      label={props.label}
      max={props.max}
      min={props.min}
      name={props.name}
      placeholder={props.placeholder}
      required={props.required}
      status={props.status}
      type={props.type}
      value={props.value}
      onChange={props.onChange}
      __private={{ ...privateProps, ariaLabelledBy: labelId }}
    />
  );
}

function TextAreaControl({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldTextAreaProps;
}) {
  const { hint, layout, type, ...textAreaProps } = props;

  return (
    <TextArea
      {...textAreaProps}
      __private={{ ...privateProps, ariaLabelledBy: labelId }}
    />
  );
}

function FileInputControl({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldFileInputProps;
}) {
  const { hint, layout, type, ...fileInputProps } = props;

  return (
    <FileInput
      {...fileInputProps}
      __private={{ ...privateProps, ariaLabelledBy: labelId }}
    />
  );
}

function RadioGroupControl<V>({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldRadioGroupProps<V>;
}) {
  return (
    <RadioGroup<V>
      disabled={props.disabled}
      label={props.label}
      name={props.name}
      options={props.options}
      required={props.required}
      status={props.status}
      value={props.value}
      onChange={props.onChange}
      __private={{
        ariaDescribedBy: privateProps.ariaDescribedBy,
        ariaLabelledBy: labelId,
        id: privateProps.id,
      }}
    />
  );
}

function CheckboxGroupControl<V>({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldCheckboxGroupProps<V>;
}) {
  return (
    <CheckboxGroup<V>
      disabled={props.disabled}
      label={props.label}
      name={props.name}
      options={props.options}
      required={props.required}
      value={props.value}
      onChange={props.onChange}
      __private={{
        ariaDescribedBy: privateProps.ariaDescribedBy,
        ariaLabelledBy: labelId,
        id: privateProps.id,
      }}
    />
  );
}

function CompoundInputControl<C extends CompoundInputValue>({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldCompoundInputProps<C>;
}) {
  return (
    <CompoundInput<C>
      disabled={props.disabled}
      label={props.label}
      name={props.name}
      required={props.required}
      segments={props.segments}
      stackOrder={props.stackOrder}
      status={props.status}
      value={props.value}
      onChange={props.onChange}
      __private={{
        ariaDescribedBy: privateProps.ariaDescribedBy,
        ariaLabelledBy: labelId,
        id: privateProps.id,
      }}
    />
  );
}

export function FormFieldControl<V, C extends CompoundInputValue>({
  labelId,
  privateProps,
  props,
}: {
  labelId: string;
  privateProps: ControlPrivateProps;
  props: FormFieldProps<V, C>;
}) {
  if (isSelectFormField(props)) {
    return (
      <SelectControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  if (isRadioGroupFormField(props)) {
    return (
      <RadioGroupControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  if (isCheckboxGroupFormField(props)) {
    return (
      <CheckboxGroupControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  if (isCompoundInputFormField(props)) {
    return (
      <CompoundInputControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  if (isTextAreaFormField(props)) {
    return (
      <TextAreaControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  if (isFileInputFormField(props)) {
    return (
      <FileInputControl
        labelId={labelId}
        privateProps={privateProps}
        props={props}
      />
    );
  }

  return (
    <InputControl
      labelId={labelId}
      privateProps={privateProps}
      props={props as FormFieldInputProps<InputType>}
    />
  );
}
